import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { COR_PRIMARIA_PDF } from '@/nucleo/pdf/cores';

interface LinhaParaExportacao {
  contrato: string;
  dreCodigo: string;
  tipoLabel: string;
  processoSei: string | null;
  valor: number;
  periodoInicio: string;
  periodoFim: string;
  atestePrevisto: string;
  credenciadoNome: string;
}

function formatarDinheiro(valor: number): string {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(iso: string): string {
  const data = new Date(`${iso}T00:00:00`);
  return Number.isNaN(data.getTime()) ? '—' : data.toLocaleDateString('pt-BR');
}

/** Exporta o resumo filtrado para XLSX, mesmo padrão de `portal-credenciado/extrato/exportar.ts`. */
export function exportarResumoAprovacaoParaXlsx(
  linhas: readonly LinhaParaExportacao[],
  competencia: string,
): void {
  const planilha = XLSX.utils.json_to_sheet(
    linhas.map((linha) => ({
      Contrato: linha.contrato,
      DRE: linha.dreCodigo,
      Tipo: linha.tipoLabel,
      'Processo SEI': linha.processoSei ?? '—',
      Valor: linha.valor,
      'Início do período': formatarData(linha.periodoInicio),
      'Fim do período': formatarData(linha.periodoFim),
      'Ateste previsto': formatarData(linha.atestePrevisto),
      'Contratado / Empresa': linha.credenciadoNome,
    })),
  );
  const livro = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(livro, planilha, 'Resumo');

  const metadados = XLSX.utils.json_to_sheet([
    { Campo: 'Competência', Valor: competencia },
    { Campo: 'Contratos', Valor: linhas.length },
    { Campo: 'Emitido em', Valor: new Date().toLocaleString('pt-BR') },
  ]);
  XLSX.utils.book_append_sheet(livro, metadados, 'Metadados');

  XLSX.writeFile(livro, `resumo-aprovacao-sme-${competencia}.xlsx`);
}

/** Exporta o resumo filtrado para PDF, tabela em paisagem. */
export function exportarResumoAprovacaoParaPdf(
  linhas: readonly LinhaParaExportacao[],
  competencia: string,
): void {
  const documento = new jsPDF({ orientation: 'landscape' });

  documento.setFontSize(14);
  documento.text('Resumo para Aprovação SME', 14, 16);
  documento.setFontSize(10);
  documento.text(`Competência: ${competencia}`, 14, 24);
  documento.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, 14, 30);

  autoTable(documento, {
    startY: 36,
    head: [['Contrato', 'DRE', 'Tipo', 'SEI', 'Valor', 'Período', 'Ateste', 'Contratado']],
    body: linhas.map((linha) => [
      linha.contrato,
      linha.dreCodigo,
      linha.tipoLabel,
      linha.processoSei ?? '—',
      formatarDinheiro(linha.valor),
      `${formatarData(linha.periodoInicio)} a ${formatarData(linha.periodoFim)}`,
      formatarData(linha.atestePrevisto),
      linha.credenciadoNome,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: COR_PRIMARIA_PDF },
  });

  documento.save(`resumo-aprovacao-sme-${competencia}.pdf`);
}
